import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Table, Tabs, Tab, Alert, Spinner, Modal, Badge, Form } from 'react-bootstrap';
import { tournamentService, athleteService } from '../../services/firebaseService';
import SimpleImportAthletes from './SimpleImportAthletes';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const SimpleTournamentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('info');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGenerateMatchesModal, setShowGenerateMatchesModal] = useState(false);
  const [showManualMatchModal, setShowManualMatchModal] = useState(false);
  const [showResetMatchesModal, setShowResetMatchesModal] = useState(false);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [showEditMatchModal, setShowEditMatchModal] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [victoryType, setVictoryType] = useState('');
  const [matchConfig, setMatchConfig] = useState({
    startTime: '09:00',
    matchDuration: 15,
    distributeByTatami: true
  });
  const [newMatch, setNewMatch] = useState({
    category: '',
    round: 1,
    athlete1: '',
    athlete2: '',
    status: 'scheduled',
    scheduledTime: '',
    tatamiNumber: 1
  });
  const [resetOption, setResetOption] = useState('all');

  // Récupérer les détails du tournoi
  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        setLoading(true);
        const tournamentData = await tournamentService.getTournamentById(id);
        
        if (!tournamentData) {
          navigate('/tournaments');
          return;
        }
        
        setTournament(tournamentData);
        
        // Si le tournoi a des athlètes, les récupérer
        if (tournamentData.athletes && tournamentData.athletes.length > 0) {
          // Les athlètes sont maintenant des objets complets avec leurs données
          setAthletes(tournamentData.athletes);
        }
        
        // Si le tournoi a des matchs générés, les récupérer
        if (tournamentData.matchesGenerated) {
          // Récupérer les matchs pour la première discipline
          const discipline = tournamentData.disciplines && tournamentData.disciplines.length > 0 
            ? tournamentData.disciplines[0] 
            : 'Judo';
          
          const matchesData = await tournamentService.getMatchesByTournament(id, discipline);
          setMatches(matchesData);
          setSelectedDiscipline(discipline);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des détails du tournoi:', err);
        setError('Impossible de charger les détails du tournoi. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTournamentDetails();
    }
  }, [id, navigate]);

  // Gérer la génération des matchs
  const handleGenerateMatches = async () => {
    try {
      setLoading(true);
      
      // Vérifier que la discipline est sélectionnée
      if (!selectedDiscipline) {
        setError('Veuillez sélectionner une discipline.');
        setLoading(false);
        return;
      }
      
      // Vérifier que le tournoi a des athlètes
      if (!tournament.athletes || tournament.athletes.length < 2) {
        setError('Le tournoi doit avoir au moins 2 athlètes pour générer des matchs.');
        setLoading(false);
        return;
      }
      
      // Générer les matchs avec les options de configuration
      const generatedMatches = await tournamentService.generateMatches(id, selectedDiscipline, {
        startTime: matchConfig.startTime,
        matchDuration: matchConfig.matchDuration,
        distributeByTatami: matchConfig.distributeByTatami
      });
      
      // Mettre à jour les matchs
      setMatches(generatedMatches);
      
      // Mettre à jour le tournoi
      const updatedTournament = await tournamentService.getTournamentById(id);
      setTournament(updatedTournament);
      
      // Fermer le modal
      setShowGenerateMatchesModal(false);
      setError(null);
      
      // Passer à l'onglet des matchs
      setActiveTab('matches');
    } catch (err) {
      console.error('Erreur lors de la génération des matchs:', err);
      setError(err.message || 'Impossible de générer les matchs. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  // Gérer la création manuelle d'un match
  const handleCreateMatch = async () => {
    try {
      setLoading(true);
      
      // Vérifier que la discipline est sélectionnée
      if (!selectedDiscipline) {
        setError('Veuillez sélectionner une discipline.');
        setLoading(false);
        return;
      }
      
      // Vérifier que les athlètes sont différents
      if (newMatch.athlete1 === newMatch.athlete2 && newMatch.athlete1 !== '') {
        setError('Veuillez sélectionner deux athlètes différents.');
        setLoading(false);
        return;
      }
      
      // Vérifier qu'au moins un athlète est sélectionné
      if (!newMatch.athlete1 && !newMatch.athlete2) {
        setError('Veuillez sélectionner au moins un athlète.');
        setLoading(false);
        return;
      }
      
      // Vérifier que la catégorie est renseignée
      if (!newMatch.category.trim()) {
        setError('Veuillez spécifier une catégorie.');
        setLoading(false);
        return;
      }
      
      // Préparer les données du match
      const matchData = {
        tournamentId: id,
        discipline: selectedDiscipline,
        category: newMatch.category,
        round: parseInt(newMatch.round) || 1,
        athlete1: newMatch.athlete1 || null,
        athlete2: newMatch.athlete2 || null,
        status: newMatch.status || 'scheduled',
        scheduledTime: newMatch.scheduledTime || null,
        tatamiNumber: parseInt(newMatch.tatamiNumber) || 1,
        winner: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: tournament.createdBy
      };
      
      // Si un seul athlète est sélectionné, c'est un bye (exempt)
      if ((matchData.athlete1 && !matchData.athlete2) || (!matchData.athlete1 && matchData.athlete2)) {
        matchData.status = 'bye';
        matchData.winner = matchData.athlete1 || matchData.athlete2;
      }
      
      // Créer le match
      const createdMatch = await tournamentService.createMatch(matchData);
      
      // Mettre à jour les matchs
      const updatedMatches = await tournamentService.getMatchesByTournament(id, selectedDiscipline);
      setMatches(updatedMatches);
      
      // Mettre à jour le tournoi pour indiquer que des matchs ont été générés
      if (!tournament.matchesGenerated) {
        await tournamentService.updateTournament(id, { matchesGenerated: true });
        const updatedTournament = await tournamentService.getTournamentById(id);
        setTournament(updatedTournament);
      }
      
      // Réinitialiser le formulaire et fermer le modal
      setNewMatch({
        category: '',
        round: 1,
        athlete1: '',
        athlete2: '',
        status: 'scheduled',
        scheduledTime: '',
        tatamiNumber: 1
      });
      setShowManualMatchModal(false);
      setError(null);
      
      // Passer à l'onglet des matchs
      setActiveTab('matches');
    } catch (err) {
      console.error('Erreur lors de la création du match:', err);
      setError(err.message || 'Impossible de créer le match. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  // Gérer les changements dans le formulaire de création de match
  const handleMatchFormChange = (e) => {
    const { name, value } = e.target;
    setNewMatch(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Gérer la réinitialisation des matchs
  const handleResetMatches = async () => {
    try {
      setLoading(true);
      
      // Confirmer que l'utilisateur souhaite réinitialiser les matchs
      if (resetOption === 'all') {
        // Réinitialiser tous les matchs du tournoi
        await tournamentService.resetMatches(id);
      } else if (resetOption === 'discipline' && selectedDiscipline) {
        // Réinitialiser uniquement les matchs de la discipline sélectionnée
        await tournamentService.resetMatches(id, selectedDiscipline);
      } else {
        setError('Veuillez sélectionner une discipline.');
        setLoading(false);
        return;
      }
      
      // Mettre à jour le tournoi
      const updatedTournament = await tournamentService.getTournamentById(id);
      setTournament(updatedTournament);
      
      // Réinitialiser les matchs
      if (resetOption === 'all') {
        setMatches([]);
      } else if (resetOption === 'discipline' && selectedDiscipline) {
        // Récupérer les matchs restants (d'autres disciplines)
        const remainingMatches = await tournamentService.getMatchesByTournament(id, selectedDiscipline);
        setMatches(remainingMatches);
      }
      
      // Fermer le modal
      setShowResetMatchesModal(false);
      setError(null);
      
      // Afficher un message de succès temporaire
      setError(`Les matchs ont été réinitialisés avec succès.`);
      setTimeout(() => setError(null), 3000);
      
    } catch (err) {
      console.error('Erreur lors de la réinitialisation des matchs:', err);
      setError(err.message || 'Impossible de réinitialiser les matchs. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le modal de sélection du type de victoire
  const openVictoryModal = (match, winnerId) => {
    setCurrentMatch(match);
    setSelectedWinner(winnerId);
    setVictoryType('');
    setShowVictoryModal(true);
  };

  // Ouvrir le modal d'édition de match
  const handleOpenEditMatchModal = (match) => {
    setCurrentMatch(match);
    setShowEditMatchModal(true);
  };

  // Mettre à jour un match
  const handleUpdateMatch = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (!currentMatch || !currentMatch.id) {
        setError('Match non valide');
        setLoading(false);
        return;
      }
      
      // Préparer les données de mise à jour
      const updateData = {
        scheduledTime: currentMatch.scheduledTime,
        tatamiNumber: parseInt(currentMatch.tatamiNumber) || 1
      };
      
      // Mettre à jour le match
      await tournamentService.updateMatch(currentMatch.id, updateData);
      
      // Rafraîchir les matchs
      const updatedMatches = await tournamentService.getMatchesByTournament(tournament.id, selectedDiscipline);
      setMatches(updatedMatches);
      
      // Fermer le modal
      setShowEditMatchModal(false);
      setCurrentMatch(null);
      setError(null);
      
      // Afficher un message de succès
      alert('Match mis à jour avec succès');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du match:', err);
      setError('Impossible de mettre à jour le match. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la mise à jour du résultat d'un match
  const handleUpdateMatchResult = async () => {
    if (!currentMatch || !selectedWinner || !victoryType) {
      setError('Veuillez sélectionner un type de victoire');
      return;
    }

    try {
      setLoading(true);
      
      // Mettre à jour le match
      await tournamentService.updateMatch(currentMatch.id, {
        winner: selectedWinner,
        victoryType: victoryType,
        status: 'completed'
      });
      
      // Attendre un court instant pour permettre la génération des matchs du tour suivant
      setTimeout(async () => {
        try {
          // Mettre à jour les matchs pour inclure les nouveaux matchs générés pour le tour suivant
          const updatedMatches = await tournamentService.getMatchesByTournament(id, selectedDiscipline);
          setMatches(updatedMatches);
          setLoading(false);
          
          // Afficher un message de succès si des matchs du tour suivant ont été générés
          const currentRound = currentMatch.round;
          const nextRoundMatches = updatedMatches.filter(m => m.round > currentRound);
          
          if (nextRoundMatches.length > 0) {
            // Utiliser une alerte temporaire pour informer l'utilisateur
            setSuccess(`Les matchs du tour suivant ont été générés automatiquement.`);
            setTimeout(() => setSuccess(''), 5000); // Effacer le message après 5 secondes
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des matchs mis à jour:', error);
        }
      }, 1000); // Attendre 1 seconde pour permettre la génération des matchs du tour suivant
      
      // Fermer le modal
      setShowVictoryModal(false);
      setCurrentMatch(null);
      setSelectedWinner(null);
      setVictoryType('');
      
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du match:', err);
      setError(err.message || 'Impossible de mettre à jour le match. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Gérer l'importation d'athlètes
  const handleImportSuccess = async (importedAthletes) => {
    try {
      // Mettre à jour le tournoi pour récupérer les athlètes importés
      const updatedTournament = await tournamentService.getTournamentById(id);
      setTournament(updatedTournament);
      
      // Mettre à jour la liste des athlètes
      if (updatedTournament.athletes && updatedTournament.athletes.length > 0) {
        setAthletes(updatedTournament.athletes);
      }
      
      // Fermer le modal
      setShowImportModal(false);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la mise à jour des athlètes:', err);
      setError(err.message || 'Impossible de mettre à jour les athlètes. Veuillez réessayer.');
    }
  };

  // Fonctions utilitaires
  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Brouillon';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      default: return 'Inconnu';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'draft': return 'secondary';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'light';
    }
  };

  const getMatchStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return 'Programmé';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'bye': return 'Exempt';
      default: return 'Inconnu';
    }
  };

  // Fonction pour obtenir le nom d'un athlète à partir de son ID
  const getAthleteName = (athleteId, athletesList) => {
    // Rechercher l'athlète dans la liste
    const athlete = athletesList.find(a => a.id === athleteId);
    if (athlete) {
      return `${athlete.firstName} ${athlete.lastName}`;
    }
    return athleteId;
  };

  // Fonction pour obtenir le nom court d'un athlète
  const getAthleteShortName = (athleteId, athletesList) => {
    // Rechercher l'athlète dans la liste
    const athlete = athletesList.find(a => a.id === athleteId);
    if (athlete) {
      return `${athlete.firstName.charAt(0)}${athlete.lastName.charAt(0)}`;
    }
    return athleteId.substring(0, 3);
  };

  // Fonction pour regrouper les matchs par catégorie et par tour
  const groupMatchesByCategoryAndRound = (matches) => {
    const grouped = {};
    
    matches.forEach(match => {
      if (!grouped[match.category]) {
        grouped[match.category] = {};
      }
      
      if (!grouped[match.category][match.round]) {
        grouped[match.category][match.round] = [];
      }
      
      grouped[match.category][match.round].push(match);
    });
    
    return grouped;
  };
  
  // Fonction pour exporter les matchs en PDF
  const exportMatchesToPDF = () => {
    if (!matches || matches.length === 0) {
      setError('Aucun match à exporter');
      return;
    }
    
    try {
      // Créer un nouveau document PDF
      const doc = new jsPDF();
      
      // Ajouter le titre
      doc.setFontSize(18);
      doc.text(`Matchs du tournoi: ${tournament.name}`, 14, 22);
      
      // Ajouter les informations du tournoi
      doc.setFontSize(12);
      doc.text(`Discipline: ${selectedDiscipline}`, 14, 30);
      doc.text(`Date: ${tournament.date ? new Date(tournament.date).toLocaleDateString() : 'Non spécifiée'}`, 14, 36);
      doc.text(`Lieu: ${tournament.location || 'Non spécifié'}`, 14, 42);
      
      // Grouper les matchs par catégorie et par tour
      const groupedMatches = groupMatchesByCategoryAndRound(matches);
      
      let yPosition = 50;
      
      // Pour chaque catégorie
      Object.entries(groupedMatches).forEach(([category, rounds]) => {
        // Ajouter un titre pour la catégorie
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        
        // Vérifier si nous avons besoin d'une nouvelle page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.text(`Catégorie: ${category}`, 14, yPosition);
        yPosition += 8;
        
        // Pour chaque tour
        Object.entries(rounds).forEach(([round, matchesList]) => {
          // Ajouter un titre pour le tour
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          
          // Vérifier si nous avons besoin d'une nouvelle page
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.text(`Tour ${round}`, 14, yPosition);
          yPosition += 6;
          
          // Créer un tableau pour les matchs
          const tableColumn = ["N°", "Heure", "Tatami", "Coin Rouge", "Coin Bleu", "Statut", "Vainqueur", "Type de victoire"];
          const tableRows = [];
          
          // Ajouter les matchs au tableau
          matchesList.forEach((match, index) => {
            const athlete1Name = match.athlete1 ? getAthleteName(match.athlete1, athletes) : 'Exempt';
            const athlete2Name = match.athlete2 ? getAthleteName(match.athlete2, athletes) : 'Exempt';
            const status = getMatchStatusLabel(match.status);
            const winner = match.winner ? getAthleteName(match.winner, athletes) : '-';
            const victoryType = match.victoryType || '-';
            const scheduledTime = match.scheduledTime || '-';
            const tatamiNumber = match.tatamiNumber ? `T${match.tatamiNumber}` : '-';
            
            tableRows.push([index + 1, scheduledTime, tatamiNumber, athlete1Name, athlete2Name, status, winner, victoryType]);
          });
          
          // Ajouter le tableau au document
          autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: yPosition,
            theme: 'striped',
            headStyles: { fillColor: [41, 128, 185], textColor: 255 },
            alternateRowStyles: { fillColor: [240, 240, 240] },
            margin: { top: 10 }
          });
          
          // Mettre à jour la position Y
          yPosition = doc.lastAutoTable.finalY + 10;
        });
      });
      
      // Ajouter la date de génération
      const today = new Date();
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Document généré le ${today.toLocaleDateString()} à ${today.toLocaleTimeString()}`, 14, 280);
      
      // Enregistrer le PDF
      doc.save(`matchs_${tournament.name.replace(/\s+/g, '_')}_${selectedDiscipline.replace(/\s+/g, '_')}.pdf`);
      
    } catch (err) {
      console.error('Erreur lors de l\'export PDF:', err);
      setError('Impossible de générer le PDF. Veuillez réessayer.');
    }
  };

  if (loading && !tournament) {
    return (
      <Container className="my-4">
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          Tournoi non trouvé ou vous n'avez pas accès à ce tournoi.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <h2>{tournament.name}</h2>
          <p className="text-muted">
            {tournament.location} - {
              tournament.startDate ? (
                typeof tournament.startDate === 'object' && tournament.startDate.toDate ? 
                  tournament.startDate.toDate().toLocaleDateString() : 
                  new Date(tournament.startDate).toLocaleDateString()
              ) : tournament.date ? (
                typeof tournament.date === 'object' && tournament.date.toDate ? 
                  tournament.date.toDate().toLocaleDateString() : 
                  new Date(tournament.date).toLocaleDateString()
              ) : 'Date non spécifiée'
            }
          </p>
        </Col>
        <Col xs="auto">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/tournaments')}
          >
            Retour aux tournois
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="info" title="Informations">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Détails du tournoi</h5>
                  <p><strong>Nom:</strong> {tournament.name}</p>
                  <p><strong>Lieu:</strong> {tournament.location || 'Non spécifié'}</p>
                  <p>
                    <strong>Date de début:</strong> {
                      tournament.startDate ? (
                        typeof tournament.startDate === 'object' && tournament.startDate.toDate ? 
                          tournament.startDate.toDate().toLocaleDateString() : 
                          new Date(tournament.startDate).toLocaleDateString()
                      ) : tournament.date ? (
                        typeof tournament.date === 'object' && tournament.date.toDate ? 
                          tournament.date.toDate().toLocaleDateString() : 
                          new Date(tournament.date).toLocaleDateString()
                      ) : 'Date non spécifiée'
                    }
                  </p>
                  <p>
                    <strong>Date de fin:</strong> {
                      tournament.endDate ? (
                        typeof tournament.endDate === 'object' && tournament.endDate.toDate ? 
                          tournament.endDate.toDate().toLocaleDateString() : 
                          new Date(tournament.endDate).toLocaleDateString()
                      ) : 'Non spécifiée'
                    }
                  </p>
                  <p>
                    <strong>Nombre de tatamis/rings:</strong> {tournament.tatamiCount || 1}
                  </p>
                  <p>
                    <strong>Statut:</strong> <Badge bg={getStatusBadgeColor(tournament.status)}>{getStatusLabel(tournament.status)}</Badge>
                  </p>
                  <p><strong>Disciplines:</strong> {tournament.disciplines ? tournament.disciplines.join(', ') : 'Judo'}</p>
                  {tournament.description && (
                    <div>
                      <h5>Description</h5>
                      <p>{tournament.description}</p>
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  <h5>Statistiques</h5>
                  <p><strong>Athlètes:</strong> {tournament.athletes ? tournament.athletes.length : 0}</p>
                  <p><strong>Matchs générés:</strong> {tournament.matchesGenerated ? 'Oui' : 'Non'}</p>
                  <p>
                    <strong>Créé le:</strong> {tournament.createdAt instanceof Date 
                      ? tournament.createdAt.toLocaleDateString() 
                      : tournament.createdAt ? new Date(tournament.createdAt).toLocaleDateString() : 'Date inconnue'}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="athletes" title="Athlètes">
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          ) : (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-3">
                <h4>Liste des athlètes ({athletes.length})</h4>
                <Button variant="primary" onClick={() => setShowImportModal(true)}>
                  Ajouter des athlètes
                </Button>
              </div>
              
              {athletes.length === 0 ? (
                <Alert variant="info">
                  Aucun athlète n'a encore été ajouté à ce tournoi.
                </Alert>
              ) : (
                <>
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher un athlète..."
                      onChange={(e) => {
                        // Fonction de recherche (pourrait être implémentée plus tard)
                      }}
                    />
                  </div>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Genre</th>
                        <th>Poids (kg)</th>
                        <th>Année de naissance</th>
                        <th>Club</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {athletes.map(athlete => (
                        <tr key={athlete.id}>
                          <td>{athlete.firstName} {athlete.lastName}</td>
                          <td>{athlete.gender}</td>
                          <td>{athlete.weight}</td>
                          <td>{athlete.birthYear}</td>
                          <td>{athlete.club || '-'}</td>
                          <td>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => {
                                // Fonction pour retirer l'athlète du tournoi
                              }}
                            >
                              Retirer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          )}
        </Tab>

        <Tab eventKey="matches" title="Matchs">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Matchs du tournoi</h4>
            <div className="d-flex gap-2 flex-wrap">
              <Button 
                variant="primary" 
                onClick={() => setShowGenerateMatchesModal(true)}
                disabled={!tournament.athletes || tournament.athletes.length < 2}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-diagram-3 me-2" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M6 3.5A1.5 1.5 0 0 1 7.5 2h1A1.5 1.5 0 0 1 10 3.5v1A1.5 1.5 0 0 1 8.5 6v1H14a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-1 0V8h-5v.5a.5.5 0 0 1-1 0V8h-5v.5a.5.5 0 0 1-1 0v-1A.5.5 0 0 1 2 7h5.5V6A1.5 1.5 0 0 1 6 4.5v-1zM8.5 5a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1zM0 11.5A1.5 1.5 0 0 1 1.5 10h1A1.5 1.5 0 0 1 4 11.5v1A1.5 1.5 0 0 1 2.5 14h-1A1.5 1.5 0 0 1 0 12.5v-1zm1.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1zm4.5.5A1.5 1.5 0 0 1 7.5 10h1a1.5 1.5 0 0 1 1.5 1.5v1A1.5 1.5 0 0 1 8.5 14h-1A1.5 1.5 0 0 1 6 12.5v-1zm1.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1zm4.5.5a1.5 1.5 0 0 1 1.5-1.5h1a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-1a1.5 1.5 0 0 1-1.5-1.5v-1zm1.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1z"/>
                </svg>
                Générer des matchs automatiquement
              </Button>
              <Button 
                variant="outline-primary" 
                onClick={() => setShowManualMatchModal(true)}
                disabled={!tournament.athletes || tournament.athletes.length === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus-circle me-2" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                </svg>
                Créer un match manuellement
              </Button>
              {tournament.matchesGenerated && (
                <Button 
                  variant="outline-danger" 
                  onClick={() => setShowResetMatchesModal(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-repeat me-2" viewBox="0 0 16 16">
                    <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                    <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                  </svg>
                  Réinitialiser les matchs
                </Button>
              )}
              {matches.length > 0 && (
                <Button 
                  variant="outline-primary" 
                  onClick={exportMatchesToPDF}
                  className="d-flex align-items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-pdf me-2" viewBox="0 0 16 16">
                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                    <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"/>
                  </svg>
                  Exporter en PDF
                </Button>
              )}
            </div>
          </div>
          
          {success && (
            <Alert variant="success" className="mb-3">
              {success}
            </Alert>
          )}
          
          {matches.length === 0 ? (
            <Alert variant="info">
              Aucun match n'a encore été généré pour ce tournoi. Cliquez sur le bouton "Générer des matchs" pour créer les matchs.
            </Alert>
          ) : (
            <div>
              {Object.entries(groupMatchesByCategoryAndRound(matches)).map(([category, rounds]) => (
                <Card key={category} className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Catégorie: {category}</h5>
                  </Card.Header>
                  <Card.Body>
                    {Object.entries(rounds).map(([round, roundMatches]) => (
                      <div key={round} className="mb-4">
                        <h6>Tour {round}</h6>
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Heure</th>
                              <th>Tatami</th>
                              <th>Phase</th>
                              <th>Athlète 1</th>
                              <th>Athlète 2</th>
                              <th>Statut</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {roundMatches.map((match, index) => (
                              <tr key={match.id}>
                                <td>{index + 1}</td>
                                <td>{match.scheduledTime || '-'}</td>
                                <td>{match.tatamiNumber ? `T${match.tatamiNumber}` : '-'}</td>
                                <td>{match.phase || '-'}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <span className="badge bg-danger me-2">Rouge</span>
                                    {match.athlete1 ? getAthleteName(match.athlete1, athletes) : 'Exempt'}
                                  </div>
                                </td>
                                <td>
                                  {match.athlete2 ? (
                                    <div className="d-flex align-items-center">
                                      <span className="badge bg-primary me-2">Bleu</span>
                                      {getAthleteName(match.athlete2, athletes)}
                                    </div>
                                  ) : 'Exempt'}
                                </td>
                                <td>{getMatchStatusLabel(match.status)}</td>
                                <td>
                                  {match.winner && (
                                    <div>
                                      <div className="fw-bold">Vainqueur:</div>
                                      <div className="d-flex align-items-center">
                                        <span className={`badge ${match.winner === match.athlete1 ? 'bg-danger' : 'bg-primary'} me-2`}>
                                          {match.winner === match.athlete1 ? 'Rouge' : 'Bleu'}
                                        </span>
                                        {getAthleteName(match.winner, athletes)}
                                      </div>
                                      {match.victoryType && (
                                        <div className="small text-muted mt-1">
                                          Victoire par {match.victoryType}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <div className="d-flex gap-2 mt-2">
                                    {!match.winner && match.status !== 'bye' && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setCurrentMatch(match) || setShowVictoryModal(true)}
                                      >
                                        Résultat
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => handleOpenEditMatchModal(match)}
                                    >
                                      Modifier
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Tab>
      </Tabs>

      {/* Modal d'importation d'athlètes */}
      <Modal 
        show={showImportModal} 
        onHide={() => setShowImportModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Ajouter des athlètes au tournoi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SimpleImportAthletes 
            tournamentId={id} 
            onImportSuccess={handleImportSuccess} 
          />
        </Modal.Body>
      </Modal>

      {/* Modal de génération de matchs */}
      <Modal 
        show={showGenerateMatchesModal} 
        onHide={() => setShowGenerateMatchesModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Générer des matchs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Vous êtes sur le point de générer des matchs pour ce tournoi. 
            Les athlètes seront regroupés par catégories de poids et de genre.
          </p>
          
          <div className="mb-3">
            <label className="form-label">Discipline</label>
            <select 
              className="form-select"
              value={selectedDiscipline}
              onChange={(e) => setSelectedDiscipline(e.target.value)}
            >
              <option value="">Sélectionnez une discipline</option>
              {tournament.disciplines && tournament.disciplines.length > 0 ? (
                tournament.disciplines.map(discipline => (
                  <option key={discipline} value={discipline}>{discipline}</option>
                ))
              ) : (
                <option value="Judo">Judo</option>
              )}
            </select>
          </div>

          <h5 className="mt-4 mb-3">Options de planification</h5>
          
          <div className="mb-3">
            <label className="form-label">Heure de début des matchs</label>
            <input 
              type="time" 
              className="form-control" 
              value={matchConfig.startTime}
              onChange={(e) => setMatchConfig({...matchConfig, startTime: e.target.value})}
            />
            <small className="text-muted">Format 24h (ex: 09:00)</small>
          </div>
          
          <div className="mb-3">
            <label className="form-label">Durée par match (minutes)</label>
            <input 
              type="number" 
              className="form-control" 
              min="5" 
              max="60"
              value={matchConfig.matchDuration}
              onChange={(e) => setMatchConfig({...matchConfig, matchDuration: parseInt(e.target.value) || 15})}
            />
            <small className="text-muted">Temps alloué à chaque match, incluant les transitions</small>
          </div>
          
          <div className="mb-3 form-check">
            <input 
              type="checkbox" 
              className="form-check-input" 
              id="distributeByTatami"
              checked={matchConfig.distributeByTatami}
              onChange={(e) => setMatchConfig({...matchConfig, distributeByTatami: e.target.checked})}
            />
            <label className="form-check-label" htmlFor="distributeByTatami">
              Distribuer les matchs sur tous les tatamis disponibles ({tournament.tatamiCount || 1})
            </label>
            <div className="text-muted small">Si désactivé, les matchs seront organisés séquentiellement par catégorie</div>
          </div>
          
          {tournament.matchesGenerated && (
            <Alert variant="warning">
              Des matchs ont déjà été générés pour ce tournoi. Générer de nouveaux matchs remplacera les matchs existants.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateMatchesModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleGenerateMatches} disabled={loading}>
            {loading ? 'Génération...' : 'Générer les matchs'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal d'édition de match */}
      <Modal show={showEditMatchModal} onHide={() => setShowEditMatchModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier l'horaire et le tatami</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {currentMatch && (
            <Form onSubmit={handleUpdateMatch}>
              <Form.Group className="mb-3">
                <Form.Label>Heure du match</Form.Label>
                <Form.Control 
                  type="time" 
                  value={currentMatch.scheduledTime || ''}
                  onChange={(e) => setCurrentMatch({...currentMatch, scheduledTime: e.target.value})}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Numéro de tatami</Form.Label>
                <Form.Control 
                  type="number" 
                  min="1"
                  max={tournament?.tatamiCount || 10}
                  value={currentMatch.tatamiNumber || 1}
                  onChange={(e) => setCurrentMatch({...currentMatch, tatamiNumber: e.target.value})}
                  required
                />
              </Form.Group>
              
              <div className="d-grid gap-2">
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Enregistrer les modifications'}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditMatchModal(false)}>
            Annuler
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal de sélection du type de victoire */}
      <Modal 
        show={showVictoryModal} 
        onHide={() => setShowVictoryModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Type de victoire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentMatch && (
            <>
              <p>Sélectionnez le vainqueur du match :</p>
              
              <div className="mb-3">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="winner"
                    id="athlete1"
                    checked={selectedWinner === currentMatch.athlete1}
                    onChange={() => setSelectedWinner(currentMatch.athlete1)}
                  />
                  <label className="form-check-label" htmlFor="athlete1">
                    <span className="badge bg-danger me-2">Rouge</span>
                    {getAthleteName(currentMatch.athlete1, athletes)}
                  </label>
                </div>
                
                {currentMatch.athlete2 && (
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="winner"
                      id="athlete2"
                      checked={selectedWinner === currentMatch.athlete2}
                      onChange={() => setSelectedWinner(currentMatch.athlete2)}
                    />
                    <label className="form-check-label" htmlFor="athlete2">
                      <span className="badge bg-primary me-2">Bleu</span>
                      {getAthleteName(currentMatch.athlete2, athletes)}
                    </label>
                  </div>
                )}
              </div>
              
              {selectedWinner && (
                <div className="mb-3">
                  <label className="form-label">Type de victoire</label>
                  <select 
                    className="form-select"
                    value={victoryType}
                    onChange={(e) => setVictoryType(e.target.value)}
                  >
                    <option value="">Sélectionnez un type de victoire</option>
                    <option value="KO">KO</option>
                    <option value="TKO">TKO (Arrêt de l'arbitre)</option>
                    <option value="Soumission">Soumission</option>
                    <option value="Points">Aux points</option>
                    <option value="Décision">Décision</option>
                    <option value="Disqualification">Disqualification</option>
                    <option value="Forfait">Forfait</option>
                    <option value="Blessure">Blessure</option>
                  </select>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVictoryModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateMatchResult} disabled={loading || !selectedWinner || !victoryType}>
            {loading ? 'Mise à jour...' : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal de création manuelle de match */}
      <Modal 
        show={showManualMatchModal} 
        onHide={() => setShowManualMatchModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Créer un match manuellement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Vous pouvez créer un match manuellement en sélectionnant les athlètes et en spécifiant les détails du match.
          </p>
          
          <div className="mb-3">
            <label className="form-label">Discipline</label>
            <select 
              className="form-select"
              value={selectedDiscipline}
              onChange={(e) => setSelectedDiscipline(e.target.value)}
            >
              <option value="">Sélectionnez une discipline</option>
              {tournament.disciplines && tournament.disciplines.length > 0 ? (
                tournament.disciplines.map(discipline => (
                  <option key={discipline} value={discipline}>{discipline}</option>
                ))
              ) : (
                <option value="Judo">Judo</option>
              )}
            </select>
          </div>
          
          <div className="mb-3">
            <label className="form-label">Catégorie</label>
            <input 
              type="text" 
              className="form-control" 
              name="category"
              value={newMatch.category}
              onChange={handleMatchFormChange}
              placeholder="Ex: Homme -73kg"
            />
            <small className="text-muted">Spécifiez la catégorie de poids et le genre</small>
          </div>
          
          <div className="mb-3">
            <label className="form-label">Tour</label>
            <input 
              type="number" 
              className="form-control" 
              name="round"
              value={newMatch.round}
              onChange={handleMatchFormChange}
              min="1"
            />
            <small className="text-muted">1 pour le premier tour, 2 pour les demi-finales, etc.</small>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">
                <span className="badge bg-danger me-2">Rouge</span>
                Athlète 1
              </label>
              <select 
                className="form-select" 
                name="athlete1"
                value={newMatch.athlete1}
                onChange={handleMatchFormChange}
              >
                <option value="">Sélectionnez un athlète</option>
                {athletes.map(athlete => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.firstName} {athlete.lastName} ({athlete.gender}, {athlete.weight}kg)
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">
                <span className="badge bg-primary me-2">Bleu</span>
                Athlète 2
              </label>
              <select 
                className="form-select" 
                name="athlete2"
                value={newMatch.athlete2}
                onChange={handleMatchFormChange}
              >
                <option value="">Sélectionnez un athlète</option>
                {athletes.map(athlete => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.firstName} {athlete.lastName} ({athlete.gender}, {athlete.weight}kg)
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mb-3">
            <label className="form-label">Statut</label>
            <select 
              className="form-select" 
              name="status"
              value={newMatch.status}
              onChange={handleMatchFormChange}
            >
              <option value="scheduled">Programmé</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminé</option>
            </select>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Heure de passage</label>
              <input 
                type="time" 
                className="form-control" 
                name="scheduledTime"
                value={newMatch.scheduledTime}
                onChange={handleMatchFormChange}
              />
              <small className="text-muted">Format 24h (ex: 14:30)</small>
            </div>
            <div className="col-md-6">
              <label className="form-label">Numéro de tatami/ring</label>
              <input 
                type="number" 
                className="form-control" 
                name="tatamiNumber"
                value={newMatch.tatamiNumber}
                onChange={handleMatchFormChange}
                min="1"
                max={tournament.tatamiCount || 10}
              />
              <small className="text-muted">Surface de combat</small>
            </div>
          </div>
          
          <Alert variant="info">
            <strong>Note:</strong> Si vous sélectionnez un seul athlète, il sera automatiquement déclaré vainqueur (exempt).
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManualMatchModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleCreateMatch} disabled={loading}>
            {loading ? 'Création...' : 'Créer le match'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal de réinitialisation des matchs */}
      <Modal 
        show={showResetMatchesModal} 
        onHide={() => setShowResetMatchesModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Réinitialiser les matchs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <Alert.Heading>Attention !</Alert.Heading>
            <p>
              Vous êtes sur le point de supprimer des matchs. Cette action est irréversible.
              Tous les résultats et données associés aux matchs seront perdus.
            </p>
          </Alert>
          
          <div className="mb-3">
            <label className="form-label">Options de réinitialisation</label>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="resetOption"
                id="resetOptionAll"
                value="all"
                checked={resetOption === 'all'}
                onChange={(e) => setResetOption(e.target.value)}
              />
              <label className="form-check-label" htmlFor="resetOptionAll">
                Réinitialiser tous les matchs du tournoi
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="resetOption"
                id="resetOptionDiscipline"
                value="discipline"
                checked={resetOption === 'discipline'}
                onChange={(e) => setResetOption(e.target.value)}
              />
              <label className="form-check-label" htmlFor="resetOptionDiscipline">
                Réinitialiser uniquement les matchs de la discipline sélectionnée
              </label>
            </div>
          </div>
          
          {resetOption === 'discipline' && (
            <div className="mb-3">
              <label className="form-label">Discipline</label>
              <select 
                className="form-select"
                value={selectedDiscipline}
                onChange={(e) => setSelectedDiscipline(e.target.value)}
              >
                <option value="">Sélectionnez une discipline</option>
                {tournament.disciplines && tournament.disciplines.length > 0 ? (
                  tournament.disciplines.map(discipline => (
                    <option key={discipline} value={discipline}>{discipline}</option>
                  ))
                ) : (
                  <option value="Judo">Judo</option>
                )}
              </select>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetMatchesModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleResetMatches} disabled={loading}>
            {loading ? 'Réinitialisation...' : 'Réinitialiser les matchs'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SimpleTournamentDetail;
